import moment from "moment"
import { formatUsPhone } from "./phone"

class CallService {
  private call
  private slugToParam: { [key: string]: any } = {}

  constructor(call: any) {
    this.call = call

    this.call.task?.params?.forEach((param: any) => {
      this.slugToParam[param.slug] = param
    })
  }

  getCall() {
    return this.call
  }

  getParams() {
    return Object.keys(this.call.task_recipient_parameters)
  }

  getParamValue(paramSlug: string) {
    const trVal = this.call.task_recipient_parameters[paramSlug]
    if (trVal != null) {
      return trVal
    }

    const taskVal = this.call.task_parameters[paramSlug]
    if (taskVal != null) {
      return taskVal
    }

    return null
  }

  getParamInfo(paramSlug: string) {
    return this.slugToParam[paramSlug]
  }

  getTaskMissingDataParams() {
    const missingDataParams: { [key: string]: any } = {}
    this.call.missing_data_from_previous_call.forEach((param: any) => {
      missingDataParams[param.slug] = param
    })

    return missingDataParams
  }

  getTask() {
    return this.call.task
  }

  getRecipient() {
    return this.call.task_recipient
  }

  getTaskRecipientLongName() {
    if (this.call.gpts_call) {
      return `${this.call.gpts_call.task_short} - ${this.call.gpts_call.recipient_name}`
    }
    if (this.call.dynamic_task) {
      return `${this.call.dynamic_task.task_short} - ${this.call.dynamic_task.recipient_name}`
    }
    if (this.call.task_template) {
      return `Task template call - ${this.getFrontendName()}`
    }

    return `${this.call.task?.name} - ${this.call.task_recipient?.name}`
  }

  getPhoneNumber() {
    let phone = ""
    if (this.call?.phone_number != null) {
      phone = formatUsPhone(this.call.phone_number)
    } else if (this.call?.task?.phone != null) {
      phone = formatUsPhone(this.call.task.phone)
    } else if (this.call.task_recipient?.phone != null) {
      phone = formatUsPhone(this.call.task_recipient.phone)
    } else if (this.call.task_recipient_parameters.phone != null) {
      phone = formatUsPhone(this.call.task_recipient_parameters.phone)
    } else {
      return "N/A"
    }

    return this.call.country_code ? `+${this.call.country_code} ${phone}` : phone
  }

  getFrontendName() {
    if (this.call.task.slug === "prank_call") {
      const voice = this.call.task.additional_public_metadata.voices.find(
        (v: any) => v.slug === this.call.task_parameters["voice-slug"]
      )

      if (voice == null) {
        return "Prank Call"
      }

      const callType = voice.call_types.find((ct: any) => ct.slug === this.call.task_parameters["call-type-slug"])
      if (callType == null) {
        return "Prank Call"
      }

      return `Prank Call as ${voice.name} - ${callType.name}`
    }

    if (this.call.call_prompt_type == "text" && this.call.call_source == "api") {
      return `API Call - ${this.timestampCreated()}`
    }
    if (this.call.gpts_call) {
      return `ChatGPT Call - ${this.call.gpts_call.task_short}`
    }
    if (this.call.dynamic_task) {
      return `Custom Call - ${this.call.dynamic_task.task_short}`
    }
    if (this.call.campaign) {
      return `Campaign Call - ${this.call.campaign.name} - ${this.call.task_template.task_name} - ${this.call.task_template_instance_name}`
    }
    if (this.call.task_template) {
      if (this.call.task_template_instance_name) {
        return `Task Template Call - ${this.call.task_template.task_name} - ${this.call.task_template_instance_name}`
      } else {
        return `Task Template Call - ${this.call.task_template.task_name}`
      }
    }

    return this.call.task?.name
  }

  getFrontendDescription() {
    if (this.call.task.slug === "prank_call") {
      const voice = this.call.task.additional_public_metadata.voices.find(
        (v: any) => v.slug === this.call.task_parameters["voice-slug"]
      )
      const callType = voice.call_types.find((ct: any) => ct.slug === this.call.task_parameters["call-type-slug"])
      if (callType == null) {
        return "Prank Call"
      }

      return callType.description
    }

    if (this.call.task_template) {
      return this.call.task_template.task_description
    }

    return this.call.task?.description
  }

  getFrontendNameShort(): string {
    if (this.call.task_slug == "prank_call") {
      const voice = this.call.task.additional_public_metadata.voices.find(
        (v: any) => v.slug === this.call.task_parameters["voice-slug"]
      )

      if (voice == null) {
        return "Prank Call"
      }

      return `Prank Call as ${voice.name}`
    }

    if (this.call.call_prompt_type == "text" && this.call.call_source == "api") {
      return `API Call - ${this.timestampCreated()}`
    }
    if (this.call.gpts_call) {
      return `${this.call.gpts_call.task_short}`
    }
    if (this.call.dynamic_task) {
      return `${this.call.dynamic_task.task_short}`
    }
    if (this.call.task_template) {
      if (this.call.task_template_instance_name) {
        return `${this.call.task_template.task_name} - ${this.call.task_template_instance_name}`
      } else {
        return `${this.call.task_template.task_name}`
      }
    }

    return this.call.task?.name
  }

  getRecipientFrontendName() {
    if (this.call.call_prompt_type == "text") {
      return null
    }
    if (this.call.gpts_call) {
      return this.call.gpts_call.recipient_name
    }
    if (this.call.dynamic_task) {
      return this.call.dynamic_task.recipient_name
    }
    if (this.call.task_template) {
      return this.call.task_recipient_parameters?.recipient_name || this.call.task_template.recipient_name || "Anyone"
      // return this.call.task_recipient_parameters.recipient_name || this.call.task_recipient?.name || "Anyone"
    }

    return this.call?.task_recipient?.name
  }

  getDescriptionShortFrontend(): string {
    if (this.call.task?.slug === "prank_call") {
      const voice = this.call.task.additional_public_metadata.voices.find(
        (v: any) => v.slug === this.call.task_parameters["voice-slug"]
      )

      if (voice == null) {
        return "Prank Call"
      }

      const callType = voice.call_types.find((ct: any) => ct.slug === this.call.task_parameters["call-type-slug"])
      if (callType == null) {
        return `Prank Call as ${voice.name}`
      }

      return `Prank Call as ${voice.name} - ${callType.name}`
    }

    if (this.call.gpts_call) {
      return this.call.gpts_call.task_short
    }
    if (this.call.dynamic_task) {
      return this.call.dynamic_task.task_short
    }

    return this.call.task?.description_short
  }

  getFrontendStatus() {
    if (this.call.status === "completed") {
      if (this.call.succeeded) {
        return "successful"
      } else {
        return "failed"
      }
    }
    if (this.call.status === "in_progress") {
      return "in_progress"
    }
    if (this.call.status === "created") {
      return "created"
    }
    if (this.call.status === "queued") {
      return "queued"
    }
    if (this.call.status === "initializing") {
      return "queued"
    }
    if (this.call.status === "scheduled") {
      return "scheduled"
    }

    return "failed"
  }

  getStatusForDisplay() {
    const statusToDisplay = {
      successful: "Successful",
      failed: "Incomplete",
      in_progress: "In Progress",
      created: "Created",
      queued: "Queued",
      scheduled: "Scheduled"
    }

    return statusToDisplay[this.getFrontendStatus()]
  }

  timeAgoStarted() {
    if (this.call.started_at != null) {
      return moment(this.call.started_at).fromNow()
    }

    return "N/A"
  }

  dateStarted() {
    if (this.call.started_at != null) {
      return moment(this.call.started_at).format("MM/DD/YYYY")
    }

    return moment(this.call.created_at).format("MM/DD/YYYY")
  }

  timestampStarted() {
    if (this.call.started_at != null) {
      return moment(this.call.started_at).format("MM/DD/YYYY h:mm A")
    }

    return "N/A"
  }

  timestampCreated() {
    return moment(this.call.created_at).format("MM/DD/YYYY h:mm A")
  }

  succeeded() {
    return this.call.succeeded
  }

  isInProgress() {
    return this.call.status === "in_progress"
  }

  getDuration() {
    if (this.call.status === "in_progress" && this.call.started_at) {
      return moment().diff(moment(this.call.started_at), "seconds")
    }

    if (this.call.duration_seconds != null) {
      return this.call.duration_seconds
    }

    if (this.call.completed_at && this.call.started_at) {
      return moment(this.call.completed_at).diff(moment(this.call.started_at), "seconds")
    }

    return 0 // Return 0 for statuses where duration is not applicable or calculable yet
  }

  followUps() {
    const actions = []

    if (this.call.actions?.length > 0) {
      actions.push("needs_action")
    }

    if (this.call.succeeded) {
      return actions
    }

    if (this.call.missing_data?.length > 0) {
      actions.push("missing_data")
    }

    return actions
  }

  hasMissingData() {
    return this.call.missing_data?.length > 0
  }

  hasActions() {
    return this.call.actions?.length > 0
  }

  completed() {
    return ["completed", "errored"].includes(this.call.status)
  }

  running() {
    return ["in_progress", "queued"].includes(this.call.status)
  }

  preparing() {
    return ["created", "initializing", "scheduled", "queued"].includes(this.call.status)
  }

  getScheduledTime() {
    if (this.call.scheduled_at) {
      return moment(this.call.scheduled_at).format("MMM D, YYYY h:mm A")
    }
    return null
  }
}

export function getCallSvcs(calls: any) {
  const callsByStatus: any = {
    all: [],
    in_progress: [],
    successful: [],
    failed: [],
    missing_data: [],
    scheduled: []
  }

  if (!calls || !Array.isArray(calls)) {
    return callsByStatus
  }

  calls.forEach((call: any) => {
    const svc = new CallService(call)
    const status = svc.getFrontendStatus()

    if (status !== "queued") {
      callsByStatus[status].push(svc)
    } else {
      callsByStatus.in_progress.push(svc)
    }

    callsByStatus.all.push(svc)

    if (status === "failed" && call.missing_data?.length > 0) {
      callsByStatus.missing_data.push(svc)
    }
  })

  // Create a merged review category (similar to the dashboard implementation)
  callsByStatus.review = [...(callsByStatus.missing_data || []), ...(callsByStatus.failed || [])]

  return callsByStatus
}

export default CallService
